import type { AppDispatch, RootState } from "../app/store";
import { triggerCartRecalculate } from "../http/cartAndLoyaltyV2";
import { selectIsLoyaltyQuoteCurrent } from "../selectors/loyaltyCartSelectors";

export type QuoteStore = {
  getState: () => RootState;
  subscribe: (listener: () => void) => () => void;
  dispatch: AppDispatch;
};

/**
 * Longer than the slow calculates seen in eg (5-15 s) and than one calculate's
 * own axios timeout (30 s).
 */
const SETTLE_QUOTE_TIMEOUT_MS = 35_000;

const isIdle = (state: RootState) =>
  (state.cartLoyaltyV2Reducer.pendingOperations ?? 0) === 0;

const nextStoreChange = (store: QuoteStore, ms: number) =>
  new Promise<boolean>((resolve) => {
    const timer = setTimeout(() => {
      unsubscribe();
      resolve(false);
    }, ms);
    const unsubscribe = store.subscribe(() => {
      clearTimeout(timer);
      unsubscribe();
      resolve(true);
    });
  });

// Re-checked after every change rather than trusting the first idle moment: a
// failed calculate drops the count to 0 and its retry raises it again inside
// the same dispatch, and only the state after that dispatch is real.
async function waitUntilIdle(
  store: QuoteStore,
  deadline: number
): Promise<boolean> {
  while (!isIdle(store.getState())) {
    const remaining = deadline - Date.now();
    if (remaining <= 0 || !(await nextStoreChange(store, remaining))) {
      return false;
    }
  }

  return true;
}

/** Units of each sub-item in the cart; survives lines merging or splitting. */
const unitsBySubItem = (state: RootState) => {
  const units = new Map<string, number>();
  for (const line of state.cartLoyaltyV2Reducer.loyalty.lines) {
    units.set(line.subItemId, (units.get(line.subItemId) ?? 0) + line.quantity);
  }

  return units;
};

const lostUnits = (before: Map<string, number>, after: Map<string, number>) =>
  Array.from(before).some(
    ([subItemId, units]) => (after.get(subItemId) ?? 0) < units
  );

/** Nothing re-priced or edited the cart since `settled` was taken. */
const isStillSettled = (settled: RootState, live: RootState) =>
  settled.cartLoyaltyV2Reducer.latestCartVersion ===
    live.cartLoyaltyV2Reducer.latestCartVersion &&
  settled.cartLoyaltyV2Reducer.loyalty.result ===
    live.cartLoyaltyV2Reducer.loyalty.result &&
  settled.cartLoyaltyV2Reducer.loyalty.lines ===
    live.cartLoyaltyV2Reducer.loyalty.lines;

/**
 * Resolves with the store's state once its loyalty quote covers the cart as it
 * is now (see `isLoyaltyQuoteCurrent`), or with null when no such quote
 * arrives in time.
 *
 * Whatever decides how the order is charged -- free or paid, the payment
 * method, the create-receipt body -- reads from what this returns, never from
 * values a handler captured when it rendered: the cart footer's click is bound
 * when the layout effect last ran, so its closure can be several calculates
 * old.
 *
 * Also null when the cart lost something the guest had when they tapped. A
 * calculate that fails with a non-network error makes the reducer pop the
 * last line and the middleware retry without it; that retry's quote is
 * current, but for a cart the guest never confirmed -- a reward-only one, so
 * paying from it placed a free order without the item.
 *
 * `settled`, a state this returned earlier for the same tap, is handed back
 * (as the live state) without waiting again while nothing has moved, so a
 * checkout step that already settled does not settle twice.
 */
export async function settleLoyaltyQuote(
  store: QuoteStore,
  {
    settled,
    timeoutMs = SETTLE_QUOTE_TIMEOUT_MS,
  }: { settled?: RootState; timeoutMs?: number } = {}
): Promise<RootState | null> {
  if (settled && isStillSettled(settled, store.getState())) {
    return store.getState();
  }

  const deadline = Date.now() + timeoutMs;
  const tappedUnits = unitsBySubItem(store.getState());
  const settledFor = (state: RootState) =>
    selectIsLoyaltyQuoteCurrent(state) &&
    !lostUnits(tappedUnits, unitsBySubItem(state));

  // A cart mutation queues its calculate in a microtask; let it dispatch so
  // the version it bumps is the one compared below.
  await new Promise((resolve) => setTimeout(resolve, 0));

  if (!(await waitUntilIdle(store, deadline))) {
    return null;
  }

  if (selectIsLoyaltyQuoteCurrent(store.getState())) {
    return settledFor(store.getState()) ? store.getState() : null;
  }

  // Idle but stale: the latest calculate failed, or was still in flight when
  // the page reloaded. Ask once more instead of leaving the guest stuck; a
  // second failure ends here rather than looping on a rate-limited backend.
  store.dispatch(triggerCartRecalculate());

  if (!(await waitUntilIdle(store, deadline))) {
    return null;
  }

  const state = store.getState();

  return settledFor(state) ? state : null;
}
